import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { I18nProvider } from "@/lib/i18n";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { PlatformAdminRoute } from "@/components/PlatformAdminRoute";
import { AdminShell } from "@/components/AdminShell";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import BranchesPage from "@/pages/BranchesPage";
import AuditPage from "@/pages/AuditPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminTenantsPage from "@/pages/admin/AdminTenantsPage";
import AdminTenantDetailPage from "@/pages/admin/AdminTenantDetailPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminAuditLogPage from "@/pages/admin/AdminAuditLogPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerNewPage from "@/pages/CustomerNewPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import CustomerEditPage from "@/pages/CustomerEditPage";
import VehiclesPage from "@/pages/VehiclesPage";
import VehicleNewPage from "@/pages/VehicleNewPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import VehicleEditPage from "@/pages/VehicleEditPage";
import BaysPage from "@/pages/BaysPage";
import TechniciansPage from "@/pages/TechniciansPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import AppointmentNewPage from "@/pages/AppointmentNewPage";
import AppointmentDetailPage from "@/pages/AppointmentDetailPage";
import JobsPage from "@/pages/JobsPage";
import JobNewPage from "@/pages/JobNewPage";
import JobDetailPage from "@/pages/JobDetailPage";
import SchedulePage from "@/pages/SchedulePage";
import NotificationsPage from "@/pages/NotificationsPage";
import InventoryPage from "@/pages/InventoryPage";
import SuppliersPage, { SupplierNewPage, SupplierDetailPage } from "@/pages/SuppliersPage";
import PurchaseOrdersPage, { PurchaseOrderNewPage, PurchaseOrderDetailPage } from "@/pages/PurchaseOrdersPage";
import EstimatesPage, { EstimateNewPage, EstimateDetailPage, EstimateEditPage } from "@/pages/EstimatesPage";
import InvoicesPage, { InvoiceDetailPage } from "@/pages/InvoicesPage";
import InvoiceNewPage from "@/pages/InvoiceNewPage";
import PaymentsPage from "@/pages/PaymentsPage";
import PublicJobStatus from "@/pages/PublicJobStatus";
import PublicEstimateApproval from "@/pages/PublicEstimateApproval";
import TechDataPage from "@/pages/TechDataPage";
import TechDataDetailPage from "@/pages/TechDataDetailPage";
import TechDataNewPage from "@/pages/TechDataNewPage";
import InspectionWorkspacePage from "@/pages/inspection/InspectionWorkspacePage";
import MyInspectionsPage from "@/pages/inspection/MyInspectionsPage";
import PublicInspectionPage from "@/pages/public/PublicInspectionPage";
import RemindersPage from "@/pages/RemindersPage";
import AdminOutboxPage from "@/pages/admin/AdminOutboxPage";
import "@/App.css";

function Shell({ children }) {
    return (
        <RequireAuth>
            <AppShell>{children}</AppShell>
        </RequireAuth>
    );
}

function AdminScreen({ children }) {
    return (
        <PlatformAdminRoute>
            <AdminShell>{children}</AdminShell>
        </PlatformAdminRoute>
    );
}

export default function App() {
    return (
        <I18nProvider>
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route path="/j/:token" element={<PublicJobStatus />} />
                            <Route path="/p/estimates/:token" element={<PublicEstimateApproval />} />
                            <Route path="/inspection/:token" element={<PublicInspectionPage />} />

                            <Route path="/" element={<Shell><DashboardPage /></Shell>} />

                            {/* Phase 8.0b — Platform Super-Admin console */}
                            <Route path="/admin" element={<AdminScreen><AdminDashboardPage /></AdminScreen>} />
                            <Route path="/admin/tenants" element={<AdminScreen><AdminTenantsPage /></AdminScreen>} />
                            <Route path="/admin/tenants/:id" element={<AdminScreen><AdminTenantDetailPage /></AdminScreen>} />
                            <Route path="/admin/users" element={<AdminScreen><AdminUsersPage /></AdminScreen>} />
                            <Route path="/admin/outbox" element={<AdminScreen><AdminOutboxPage /></AdminScreen>} />
                            <Route path="/admin/audit-log" element={<AdminScreen><AdminAuditLogPage /></AdminScreen>} />
                            <Route path="/settings" element={<Shell><SettingsPage /></Shell>} />
                            <Route path="/users" element={<Shell><UsersPage /></Shell>} />
                            <Route path="/branches" element={<Shell><BranchesPage /></Shell>} />
                            <Route path="/reminders" element={<Shell><RemindersPage /></Shell>} />
                            <Route path="/audit" element={<Shell><AuditPage /></Shell>} />

                            <Route path="/customers" element={<Shell><CustomersPage /></Shell>} />
                            <Route path="/customers/new" element={<Shell><CustomerNewPage /></Shell>} />
                            <Route path="/customers/:id" element={<Shell><CustomerDetailPage /></Shell>} />
                            <Route path="/customers/:id/edit" element={<Shell><CustomerEditPage /></Shell>} />

                            <Route path="/vehicles" element={<Shell><VehiclesPage /></Shell>} />
                            <Route path="/vehicles/new" element={<Shell><VehicleNewPage /></Shell>} />
                            <Route path="/vehicles/:id" element={<Shell><VehicleDetailPage /></Shell>} />
                            <Route path="/vehicles/:id/edit" element={<Shell><VehicleEditPage /></Shell>} />

                            {/* Phase 3 */}
                            <Route path="/bays" element={<Shell><BaysPage /></Shell>} />
                            <Route path="/technicians" element={<Shell><TechniciansPage /></Shell>} />
                            <Route path="/schedule" element={<Shell><SchedulePage /></Shell>} />
                            <Route path="/appointments" element={<Shell><AppointmentsPage /></Shell>} />
                            <Route path="/appointments/new" element={<Shell><AppointmentNewPage /></Shell>} />
                            <Route path="/appointments/:id" element={<Shell><AppointmentDetailPage /></Shell>} />
                            <Route path="/jobs" element={<Shell><JobsPage /></Shell>} />
                            <Route path="/jobs/new" element={<Shell><JobNewPage /></Shell>} />
                            <Route path="/jobs/:id" element={<Shell><JobDetailPage /></Shell>} />

                            <Route path="/notifications" element={<Shell><NotificationsPage /></Shell>} />

                            {/* Phase 5 — Inventory, Suppliers, Purchase Orders */}
                            <Route path="/inventory" element={<Shell><InventoryPage /></Shell>} />
                            <Route path="/suppliers" element={<Shell><SuppliersPage /></Shell>} />
                            <Route path="/suppliers/new" element={<Shell><SupplierNewPage /></Shell>} />
                            <Route path="/suppliers/:id" element={<Shell><SupplierDetailPage /></Shell>} />
                            <Route path="/purchase-orders" element={<Shell><PurchaseOrdersPage /></Shell>} />
                            <Route path="/purchase-orders/new" element={<Shell><PurchaseOrderNewPage /></Shell>} />
                            <Route path="/purchase-orders/:id" element={<Shell><PurchaseOrderDetailPage /></Shell>} />

                            {/* Phase 6 — Estimates / Invoices / Payments */}
                            <Route path="/estimates" element={<Shell><EstimatesPage /></Shell>} />
                            <Route path="/estimates/new" element={<Shell><EstimateNewPage /></Shell>} />
                            <Route path="/estimates/:id" element={<Shell><EstimateDetailPage /></Shell>} />
                            <Route path="/estimates/:id/edit" element={<Shell><EstimateEditPage /></Shell>} />
                            <Route path="/invoices" element={<Shell><InvoicesPage /></Shell>} />
                            <Route path="/invoices/new" element={<Shell><InvoiceNewPage /></Shell>} />
                            <Route path="/invoices/:id" element={<Shell><InvoiceDetailPage /></Shell>} />
                            <Route path="/payments" element={<Shell><PaymentsPage /></Shell>} />

                            {/* Phase 7 — Technical Data */}
                            <Route path="/tech-data" element={<Shell><TechDataPage /></Shell>} />
                            <Route path="/tech-data/new" element={<Shell><TechDataNewPage /></Shell>} />
                            <Route path="/tech-data/:id" element={<Shell><TechDataDetailPage /></Shell>} />

                            {/* Phase 8.1b — Digital Vehicle Inspection */}
                            <Route path="/inspections" element={<Shell><MyInspectionsPage /></Shell>} />
                            <Route path="/jobcards/:jobcard_id/inspection" element={<Shell><InspectionWorkspacePage /></Shell>} />

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        <Toaster position="top-right" toastOptions={{ style: { fontFamily: "Inter" } }} />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </I18nProvider>
    );
}
